/* eslint-disable @typescript-eslint/no-magic-numbers */
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { RxCross2 } from 'react-icons/rx';

import useLatex from '@/hooks/useLatex';
import useDismissableDialog from '@/hooks/util/useDismissableDialog';
import useStore from '@/store';
import { cn } from '@/utils';
import { onStopPropagation } from '@/utils/helper';

export interface ButtonDialogProperties {
  open: boolean;
  title?: string;
  subtitle?: string;
  onLeftButtonClick?: () => void;
  onRightButtonClick?: () => void;
  leftButtonText?: string;
  rightButtonText?: string;
  onClose?: () => void;
  variant?: 'default' | 'blue' | 'left';
  zIndex?: number;
  showCloseButton?: boolean;
}

export type ButtonDialogBoxContextType = {
  open: boolean;
  closeDialog: () => void;
  showButtonDialogBox: (dialogProperties: ButtonDialogProperties) => void;
  updateButtonDialogProperties: (dialogProperties: ButtonDialogProperties) => void;
};

const ButtonDialogBoxContext = createContext<ButtonDialogBoxContextType | undefined>(undefined);

const onKeyDown = () => {};

export const useButtonDialogBox = () => {
  const context = useContext(ButtonDialogBoxContext);
  if (!context) {
    throw new Error('Dialog must be used within a ButtonDialogBoxProvider');
  }
  return context;
};

export function ButtonDialogBoxProvider({ children }: { children: ReactNode }) {
  const [buttonDialogProperties, setButtonDialogProperties] = useState<ButtonDialogProperties>({
    open: false,
    variant: 'default',
  });

  const {
    open,
    title,
    subtitle,
    onLeftButtonClick,
    leftButtonText,
    onRightButtonClick,
    rightButtonText,
    variant = 'default',
    zIndex = 20,
    showCloseButton = true,
  } = buttonDialogProperties;

  const onClose = useCallback(() => {
    if (buttonDialogProperties.onClose) {
      buttonDialogProperties.onClose();
    }
    setButtonDialogProperties((previous) => ({ ...previous, open: false }));
  }, [buttonDialogProperties]);

  const { dialogReference, onDismiss } = useDismissableDialog({
    isOpen: open,
    onClose,
    dismissable: true,
  });
  useLatex(title || '');
  useLatex(subtitle || '');

  const showButtonDialogBox = useCallback((dialogProperties: ButtonDialogProperties) => {
    setButtonDialogProperties(dialogProperties);
  }, []);

  const { appLanguage } = useStore();

  const value = useMemo(
    () => ({
      open,
      showButtonDialogBox,
      updateButtonDialogProperties: setButtonDialogProperties,
      closeDialog: onDismiss,
    }),
    [onDismiss, showButtonDialogBox, open],
  );

  const dialogClasses = cn('relative flex flex-col items-center justify-center rounded-xl', {
    'max-w-[665px] bg-neutral-white': variant === 'default',
    'min-w-[35rem] max-w-[40rem] bg-primary-blue-3 border-8 border-secondary-blue-3 rounded-3xl':
      variant === 'blue',
  });

  const closeButtonClasses = cn('absolute z-modal cursor-pointer', {
    'right-6 top-6': variant === 'default',
    'right-6 top-6 border-2 border-neutral-white w-10 h-10 p-1 bg-primary-blue-3 rounded-full':
      variant === 'blue',
  });

  const contentContainerClasses = cn('flex h-full w-full flex-col items-center justify-center', {
    'min-h-[15em] min-w-[35em] py-12 pl-12 pr-16': variant === 'default',
    'rounded-2xl p-10 min-h-[15em] min-w-[35em]': variant === 'blue',
  });

  const titleClasses = cn('w-full text-center text-[1.5625em] font-semibold leading-normal', {
    'text-primary-blue-3': variant === 'default',
    'text-neutral-white font-lilita text-[2.5rem]': variant === 'blue',
    'leading-normal': variant === 'blue' && appLanguage === 'en',
    'leading-[1.6]': variant === 'blue' && appLanguage !== 'en',
  });

  const subtitleClasses = cn('max-w-full pr-4 text-center !text-l1 font-light leading-normal', {
    'mt-4': title,
    'text-left': title && variant === 'left',
    '!text-neutral-grey-8 leading-[1.5] font-semibold text-xl1': variant === 'blue',
  });

  const leftButtonClasses = cn(
    'font-boldshadow-md bottom-6 z-default rounded-[72px] px-4 py-4 font-semibold shadow-custom',
    {
      'border-2 border-primary-blue-3 text-neutral-grey-4 w-[14.875rem] !text-l1':
        variant === 'default',
      'border-2 border-neutral-white text-neutral-white w-[11.875rem] !text-l1': variant === 'blue',
    },
  );

  const rightButtonClasses = cn(
    'font-boldshadow-md bottom-6 z-default ml-7 rounded-[72px] px-4 py-4 text-l1 font-semibold shadow-custom bg-primary-yellow-3 text-primary-blue-3',
    {
      'w-[14.875rem] !text-l1 ': variant === 'default',
      'w-[11.875rem] !text-l1 ': variant === 'blue',
    },
  );

  return (
    <ButtonDialogBoxContext.Provider value={value}>
      {children}
      {open && (
        <div
          className={`absolute top-0 z-${zIndex} flex h-[100vh] w-[100vw] items-center backdrop-blur-sm backdrop-brightness-50`}
          onClick={onStopPropagation}
          role="button"
          tabIndex={0}
          onKeyDown={onKeyDown}
        >
          <dialog ref={dialogReference} open={open} className={dialogClasses}>
            {showCloseButton && (
              <RxCross2
                className={closeButtonClasses}
                style={{
                  color: variant === 'default' ? '#4F4F4F' : '#FFFFFF',
                  fontSize: variant === 'default' ? '2em' : '2.5em',
                }}
                onClick={onDismiss}
              />
            )}
            <div className={contentContainerClasses}>
              <div className="custom-scrollbar flex h-full max-h-[20rem] w-full min-w-[30rem] flex-col items-center justify-start overflow-y-auto">
                {title && (
                  <p
                    className={titleClasses}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: title }}
                  />
                )}
                {subtitle && (
                  <p
                    className={subtitleClasses}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: subtitle }}
                  />
                )}
              </div>
              <div className="mt-6 flex w-full justify-center">
                {onLeftButtonClick && leftButtonText && (
                  <button type="button" onClick={onLeftButtonClick} className={leftButtonClasses}>
                    {leftButtonText}
                  </button>
                )}
                {onRightButtonClick && rightButtonText && (
                  <button type="button" onClick={onRightButtonClick} className={rightButtonClasses}>
                    {rightButtonText}
                  </button>
                )}
              </div>
            </div>
          </dialog>
        </div>
      )}
    </ButtonDialogBoxContext.Provider>
  );
}
